import { useMemo, useState } from "react"
import { Button, Col, Flex, Row, Tooltip } from "antd"
import StatusTag from "pagesV2/pro/clients/ClientDetails/ClientDetailsTabs/BookingTab/BookingTable/StatusTag"
import PropTypes from "prop-types"
import { Avatar } from "ui/Avatar"
import { Text, Title } from "ui/Typography"
import { bookingActionsBasedOnStatus, displayPaymentStatus, getFormattedBookingDate } from "../utils"
import CancelBookingModal from "pagesV2/pro/Bookings/BookingActions/CancelBookingModal"
import { useDispatch } from "react-redux"
import { getClientBookingDetails, guideBookingStatus } from "store/bookings"
import BookingConfirmationModal from "ui/BookingConfirmationModal"
import { toaster } from "components/common/Toast"
import styles from "./index.module.css"
import { routes } from "router"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import useActiveGuide from "hooks/useActiveClient"
import { getMediaUrl } from "utils/media-upload"
import { usePermissions } from "hooks/usePermissions"
import { MODULES, ACTIONS } from "utils/permissions"

const BookingDetailsHeader = ({ booking, className, onCancelSuccess }) => {
  const { id, experience, booking_id, booked_on, booking_status, payment_status, uuid, client, is_expired, can_edit, reason } = booking || {}
  const [isCancelBookingModalVisible, setIsCancelBookingModalVisible] = useState(false)
  const [isConfirmationModalVisible, setIsConfirmationModalVisible] = useState(false)
  const [bookingConfirmStatus, setBookingConfirmStatus] = useState("")
  const { t } = useTranslation()
  const { isActive: active } = useActiveGuide()
  const { hasPermission } = usePermissions()

  const dispatch = useDispatch()
  const navigate = useNavigate()

  const canCreateBooking = hasPermission(MODULES.BOOKINGS, ACTIONS.CREATE)

  const handleCancelBooking = () => {
    setIsCancelBookingModalVisible(true)
  }

  const handleAcceptBooking = () => {
    setIsConfirmationModalVisible(true)
    setBookingConfirmStatus("accept")
  }

  const handleDeclineBooking = () => {
    setIsConfirmationModalVisible(true)
    setBookingConfirmStatus("decline")
  }

  const handleBookAgain = () => {
    if (!booking?.uuid) return
    navigate(routes.guideBookingBookAgainPath({ id: booking?.uuid }))
  }

  const handleEditBooking = async () => {
    if (!booking?.uuid) return
    navigate(routes.guideBookingEditPath({ id: booking?.uuid }))
  }

  const bookingActions = useMemo(() => {
    const actions = bookingActionsBasedOnStatus({
      status: {
        booking_status: booking_status?.toLowerCase(),
        payment_status: displayPaymentStatus(payment_status).toLowerCase(),
        isExpired: is_expired
      },
      handlers: {
        cancel: handleCancelBooking,
        accept: handleAcceptBooking,
        decline: handleDeclineBooking,
        bookAgain: handleBookAgain,
        edit: handleEditBooking
      }
    })

    return actions?.filter((action) => {
      if (action.key === "edit" || action.key === "cancel") {
        return can_edit
      }
      if (action.key === "book_again") {
        return canCreateBooking
      }
      return true
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [booking_status, payment_status, can_edit, canCreateBooking])

  const bookedOn = useMemo(() => {
    return getFormattedBookingDate(booked_on)
  }, [booked_on])

  const cancelBookingData = useMemo(() => {
    return {
      id,
      image: getMediaUrl(experience?.experience_avatar),
      title: experience?.title,
      booking_id: booking_id,
      booked_on: bookedOn,
      uuid: booking?.uuid,
      client_name: booking?.client?.name,
      client_email: booking?.client?.email
    }
  }, [uuid, experience?.experience_avatar, experience?.title, booking_id, bookedOn, booking?.client?.name, booking?.client?.email])

  const handleConfirmCancel = () => {
    setIsCancelBookingModalVisible(false)
    dispatch(getClientBookingDetails(uuid))
    onCancelSuccess?.()
  }

  const handleConfirmBooking = async () => {
    setIsConfirmationModalVisible(false)
    try {
      await dispatch(guideBookingStatus(booking.uuid, bookingConfirmStatus === "accept" ? "booked" : "canceled"))
      toaster.success({ title: "Booking status updated successfully" })
      dispatch(getClientBookingDetails(uuid))
    } catch (err) {
      console.error("Error updating booking status:", err)
    }
  }

  return (
    <>
      <Row className={`${className} p-xl`}>
        <Col xs={24}>
          <Flex justify="space-between" align="center" className={`${styles.headerContainer}`}>
            <Flex align="center" gap={16} wrap="wrap">
              {getMediaUrl(experience?.experience_avatar) && (
                <Avatar fullName={experience?.title} src={getMediaUrl(experience?.experience_avatar)} shape="square" size={80} />
              )}
              <Flex vertical gap={4}>
                {experience?.title && <Title level={3}>{experience.title}</Title>}
                <Flex gap={8} className={styles.idDateTimeContainer}>
                  {booking_id && <Text>ID {booking_id}</Text>}
                  {bookedOn && (
                    <>
                      <Text>·</Text>
                      <Text>Booked on {bookedOn}</Text>
                    </>
                  )}
                </Flex>
                {reason && (
                  <Text>
                    <strong>Cancel Reason: </strong> {reason}
                  </Text>
                )}
                 {booking_status && <StatusTag status={booking_status} />}
              </Flex>
            </Flex>
            <Flex gap={12}>
              {bookingActions?.map((action) => (
                <Tooltip
                  key={action?.key}
                  title={is_expired && action?.disabled ? t("client.client_booking.trip_time_has_passed") : ""}
                  placement="top"
                  mouseEnterDelay={0.3}
                >
                  <Button
                    className="px-xl py-l"
                    onClick={active ? action?.onClick : undefined}
                    type={action?.type}
                    danger={action?.danger}
                    variant={action?.variant}
                    color={action?.color}
                    disabled={!active || action?.disabled}
                    aria-disabled={!active || action?.disabled}
                  >
                    {action?.label}
                  </Button>
                </Tooltip>
              ))}
            </Flex>
          </Flex>
        </Col>
      </Row>
      {isCancelBookingModalVisible && (
        <CancelBookingModal
          open={isCancelBookingModalVisible}
          onCancel={() => setIsCancelBookingModalVisible(false)}
          bookings={[cancelBookingData]}
          onConfirm={handleConfirmCancel}
        />
      )}
      {isConfirmationModalVisible && (
        <BookingConfirmationModal
          open={isConfirmationModalVisible}
          handleCancel={() => setIsConfirmationModalVisible(false)}
          handleConfirm={handleConfirmBooking}
          clientName={client?.name}
          tripDate={bookedOn}
          bookingConfirmStatus={bookingConfirmStatus}
        />
      )}
    </>
  )
}

BookingDetailsHeader.propTypes = {
  booking: PropTypes.shape({
    experience: PropTypes.shape({
      title: PropTypes.string,
      experience_avatar: PropTypes.arrayOf(
        PropTypes.shape({
          url: PropTypes.string
        })
      )
    }),
    id: PropTypes.string,
    booking_date: PropTypes.string,
    booking_time: PropTypes.string,
    booking_status: PropTypes.string,
    payment_status: PropTypes.string,
    uuid: PropTypes.string,
    client: PropTypes.shape({
      name: PropTypes.string,
      email: PropTypes.string
    })
  }),
  className: PropTypes.string,
  onCancelSuccess: PropTypes.func
}

export default BookingDetailsHeader
